/**
 * 解析 query-attribute-template 响应，供发品表单渲染。
 */
import i18n from '@/i18n';
import { fixedUi } from '@/i18n/fixedUi';

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    if (Array.isArray(value.list)) return value.list;
    if (Array.isArray(value.data)) return value.data;
  }
  return [];
}

/** 标量 attribute_id（排除平台 data 行上的 attribute_id: number[] 汇总字段） */
function isScalarAttributeId(value) {
  if (value == null || Array.isArray(value)) return false;
  if (typeof value === 'object') return false;
  const s = String(value).trim();
  return s !== '' && s !== 'null' && s !== 'undefined';
}

function rowProductTypeId(item) {
  if (!item || typeof item !== 'object') return '';
  const raw = item.product_type_id ?? item.productTypeId;
  if (raw == null || Array.isArray(raw)) return '';
  return String(raw).trim();
}

function wantedProductTypeId(options) {
  if (options == null) return '';
  if (typeof options === 'string' || typeof options === 'number') {
    return String(options).trim();
  }
  const raw = options.productTypeId ?? options.product_type_id;
  return raw == null ? '' : String(raw).trim();
}

function keepAttributeRow(item, wantTypeId) {
  if (!wantTypeId) return true;
  const rowType = rowProductTypeId(item);
  if (!rowType) return true;
  return rowType === wantTypeId;
}

/** 从 data[] / 顶层数组 抽出 attribute_infos；指定 productTypeId 时丢掉其他类目行 */
function mergeAttributeInfosFromRows(rows, wantTypeId = '') {
  const merged = [];
  (Array.isArray(rows) ? rows : []).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    if (!keepAttributeRow(item, wantTypeId)) return;
    // 平台行结构：{ product_type_id, attribute_infos: [...], attribute_id: [id...] }
    // 必须优先展开 attribute_infos；勿把行上的 attribute_id 数组当成单条属性
    const infos = asArray(item.attribute_infos || item.attributeInfos);
    if (infos.length) {
      infos.forEach((attr) => merged.push(attr));
      return;
    }
    // 行本身就是属性（有标量 attribute_id）
    if (isScalarAttributeId(item.attribute_id) || isScalarAttributeId(item.attributeId)) {
      merged.push(item);
    }
  });
  return merged;
}

function pickAttributePool(raw, wantTypeId = '') {
  if (!raw || typeof raw !== 'object') return [];

  // 误把 data 数组直接传入时：[{ attribute_infos: [...] }]
  if (Array.isArray(raw)) {
    const fromRows = mergeAttributeInfosFromRows(raw, wantTypeId);
    if (fromRows.length) return fromRows;
    return raw.filter((item) => item && (
      isScalarAttributeId(item.attribute_id) || isScalarAttributeId(item.attributeId)
    ));
  }

  // 常见结构：info.data[] -> { attribute_infos: [...] }
  const dataList = asArray(raw.data);
  if (dataList.length) {
    const merged = mergeAttributeInfosFromRows(dataList, wantTypeId);
    if (merged.length) return merged;
  }

  // 后端已 unwrap 的 info，或再包一层 info
  const nestedInfo = raw.info && typeof raw.info === 'object' ? raw.info : null;
  if (nestedInfo) {
    const fromInfoData = mergeAttributeInfosFromRows(asArray(nestedInfo.data), wantTypeId);
    if (fromInfoData.length) return fromInfoData;
  }

  const candidates = [
    raw.attribute_infos,
    raw.attributeInfos,
    raw.product_attribute_infos,
    raw.product_attribute_list,
    raw.attributes,
    nestedInfo?.attribute_infos,
    nestedInfo?.attributeInfos,
    nestedInfo?.data,
  ];
  for (const item of candidates) {
    if (Array.isArray(item) && item[0] && (item[0].attribute_infos || item[0].attributeInfos)) {
      const merged = mergeAttributeInfosFromRows(item, wantTypeId);
      if (merged.length) return merged;
    }
    const list = asArray(item);
    if (list.length && (
      isScalarAttributeId(list[0]?.attribute_id) || isScalarAttributeId(list[0]?.attributeId)
    )) {
      return list;
    }
  }
  return [];
}

function hasCjk(text) {
  return /[\u4e00-\u9fff]/.test(String(text || ''));
}

function normalizeAttrKey(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z\u4e00-\u9fff]/g, '')
    .trim();
}

/** 测试环境常见英文属性名 → 中文展示名 */
const ATTR_LABEL_ZH = {
  season: '季节',
  color: '颜色',
  size: '尺码',
  material: '材质',
  fabric: '面料',
  fabricweight: '面料重量',
  fabricdensity: '面料纱织/密度',
  features: '特征',
  pattern: '图案',
  patternype: '图案',
  patterntype: '图案',
  style: '风格',
  composition: '成分',
  filling: '填充物',
  careinstructions: '护理说明',
  includedcomponents: '包含的组件',
  producttechnique: '产品工艺',
  neckline: '领型',
  sleevelength: '袖长',
  fittype: '合身类型',
  height: '高度',
  length: '长度',
  width: '宽度',
  circumference: '周长',
  diameter: '直径',
  quantity: '数量',
  styletype: '款式',
};

/** 平台文案常夹杂单位/测试脏词，展示前精简 */
export function simplifyPlatformLabel(text) {
  let s = String(text || '').trim();
  if (!s) return '';
  // 去掉单位括号： (cm) / （厘） / (厘米)
  s = s.replace(/\s*[（(]\s*(cm|mm|in|inch|厘米|釐米|厘)\s*[）)]\s*/gi, ' ');
  // 去掉「(中文)」/「（中文）」语种标记
  s = s.replace(/\s*[（(]\s*中文\s*[）)]\s*/g, ' ');
  // 去掉粘连尾巴：M中文 / XL中文
  s = s.replace(/([A-Za-z0-9*×xX]+)中文/g, '$1');
  // 独立「中文」词（少见，兜底）
  s = s.replace(/(^|[\s_\-])中文(?=$|[\s_\-])/g, '$1');
  // 语种尾巴：XS-zh-cn2 / en-us
  s = s.replace(/[-_\s]?(?:zh[-_]?cn|en[-_]?us)\d*/gi, '');
  // 去掉常见测试尾巴
  s = s.replace(/\s*(?:中台\s*)?test\d*\s*$/gi, '');
  s = s.replace(/\s+(安安|aa\d+|en\d+)\s*$/gi, '');
  // 「Height 安安」：英文主体 + 短中文噪音 → 保留英文主体
  s = s.replace(/^([A-Za-z][A-Za-z0-9\s/&_.-]{1,48}?)\s+[\u4e00-\u9fff]{1,4}$/u, '$1');
  s = s.replace(/\s+/g, ' ').trim();
  // 清理去语种后残留的首尾分隔符
  s = s.replace(/^[-_\s]+|[-_\s]+$/g, '').trim();
  return s;
}

function isMostlyLatinWithCjkNoise(text) {
  const s = String(text || '');
  const cjk = (s.match(/[\u4e00-\u9fff]/g) || []).length;
  const latin = (s.match(/[A-Za-z]/g) || []).length;
  return latin >= 3 && cjk > 0 && cjk <= 4 && latin >= cjk;
}

function inferLabelFromValues(values) {
  const names = (values || []).map((v) => `${v.nameCn || ''} ${v.name || ''}`).join(' ');
  if (/四季|春|夏|秋|冬/.test(names) && /春|夏|秋|冬/.test(names)) return '季节';
  if (/黑色|白色|红色|蓝色|绿色|黄色|粉色|灰色/.test(names)) return '颜色';
  if (/\bS\b|\bM\b|\bL\b|XL|XXL|均码/.test(names)) return '尺码';
  return '';
}

function uiIsZh() {
  return String(i18n.resolvedLanguage || i18n.language || 'zh-CN').toLowerCase().startsWith('zh');
}

function latinCandidate(candidates) {
  return candidates.find((item) => {
    const cleaned = simplifyPlatformLabel(item) || item;
    return cleaned && !hasCjk(cleaned);
  });
}

function pickDisplayName(attr, values) {
  const candidates = [
    attr.attribute_name_cn,
    attr.attributeNameCn,
    attr.attribute_multi_language_name,
    attr.attribute_name,
    attr.name,
    attr.attribute_name_en,
    attr.attributeNameEn,
  ]
    .map((s) => String(s || '').trim())
    .filter(Boolean);

  if (!uiIsZh()) {
    const latin = latinCandidate(candidates);
    const picked = simplifyPlatformLabel(latin || '') || latin;
    if (picked) return picked;
    const inferredEarly = inferLabelFromValues(values);
    if (inferredEarly) return fixedUi(inferredEarly);
    const fallbackEarly = simplifyPlatformLabel(candidates[0]) || candidates[0];
    return fallbackEarly
      ? fixedUi(fallbackEarly)
      : `Attr ${attr.attribute_id ?? attr.attributeId ?? ''}`;
  }

  // 勿因「Height (cm) 安安」含两字中文就整段当中文名
  const chinese = candidates.find((s) => hasCjk(s) && !isMostlyLatinWithCjkNoise(s));
  if (chinese) return simplifyPlatformLabel(chinese) || chinese;

  for (const item of candidates) {
    const cleaned = simplifyPlatformLabel(item) || item;
    const mapped = ATTR_LABEL_ZH[normalizeAttrKey(cleaned)];
    if (mapped) return mapped;
    // Season-77 / Features(217) 这类：取字母前缀再映射
    const prefix = normalizeAttrKey(cleaned.split(/[-_(]/)[0] || cleaned);
    if (ATTR_LABEL_ZH[prefix]) return ATTR_LABEL_ZH[prefix];
  }

  const inferred = inferLabelFromValues(values);
  if (inferred) return inferred;

  const fallback = simplifyPlatformLabel(candidates[0]) || candidates[0];
  return fallback || `属性 ${attr.attribute_id ?? attr.attributeId ?? ''}`;
}

function normalizeAttribute(attr) {
  if (!attr || typeof attr !== 'object') return null;
  const attributeId = attr.attribute_id ?? attr.attributeId;
  if (attributeId == null) return null;
  const values = asArray(
    attr.attribute_value_info_list
    || attr.attribute_value_list
    || attr.attributeValueInfoList
    || attr.values,
  )
    .map((v) => {
      const cn = simplifyPlatformLabel(v.attribute_value_name || v.attribute_value_cn || '');
      const raw = simplifyPlatformLabel(v.attribute_value || v.name || '');
      const en = simplifyPlatformLabel(v.attribute_value_en || '');
      // 选项优先中文；无名称时留空，禁止用 attribute_value_id 冒充展示名
      const display = (hasCjk(cn) && cn)
        || (hasCjk(raw) && raw)
        || cn
        || raw
        || en
        || '';
      return {
        id: String(v.attribute_value_id ?? v.attributeValueId ?? ''),
        name: display,
        nameCn: hasCjk(cn) ? cn : (hasCjk(raw) ? raw : display),
      };
    })
    // 无展示名的选项会在下拉里变成空白行，直接丢弃
    .filter((v) => v.id && String(v.name || '').trim());

  // 文档/联调常见：0 手输 1 多选 2/3 单选 4 多选+比例
  let mode = Number(attr.attribute_mode ?? attr.attributeMode ?? 3);
  if (mode === 2) mode = 3;
  const status = Number(attr.attribute_status ?? attr.attributeStatus ?? 0);
  const name = pickDisplayName(attr, values);
  const nameEn = attr.attribute_name_en || attr.attributeNameEn || '';
  const rules = asArray(attr.rule_info_list || attr.ruleInfoList)
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const conditionType = Number(row.condition_type ?? row.conditionType ?? 0);
      if (!Number.isFinite(conditionType) || conditionType <= 0) return null;
      return {
        conditionType,
        conditionOperator: Number(row.condition_operator ?? row.conditionOperator ?? 0) || 0,
        value: String(row.value ?? '').trim(),
        id: row.id ?? row.rule_id ?? null,
      };
    })
    .filter(Boolean);

  return {
    attributeId: String(attributeId),
    name,
    nameEn,
    type: Number(attr.attribute_type ?? attr.attributeType ?? 0),
    label: Number(attr.attribute_label ?? attr.attributeLabel ?? 0),
    status,
    mode,
    /** 0 手输 1 多选 3 单选 4 多选+比例 */
    maxSelect: Number(attr.attribute_input_num ?? attr.attributeInputNum ?? 0) || (mode === 1 || mode === 4 ? 20 : 1),
    values,
    rules,
    required: status === 3,
    isComposition: mode === 4 || /成分|composition|material/i.test(`${name} ${nameEn}`),
  };
}

function isColorSaleAttr(attr) {
  return Number(attr?.label) === 1
    || /颜色|colour|color/i.test(`${attr?.name || ''} ${attr?.nameEn || ''}`);
}

function isSizeSaleAttr(attr) {
  return Number(attr?.label) !== 1
    && /尺码|尺寸|size/i.test(`${attr?.name || ''} ${attr?.nameEn || ''}`);
}

export function parseAttributeTemplate(raw, options) {
  const wantTypeId = wantedProductTypeId(options);
  const poolRaw = pickAttributePool(raw, wantTypeId);
  const pool = poolRaw.map(normalizeAttribute).filter(Boolean);

  const productAttrs = pool.filter((a) => a.type === 3 || a.type === 4);
  const saleAttrs = pool.filter((a) => a.type === 1);
  const sizeAttrs = pool.filter((a) => a.type === 2);

  // SKC 销售属性（通常为颜色）：优先 label=1，再按名称，再回退第一条
  let skcSaleAttrs = saleAttrs.filter((a) => Number(a.label) === 1);
  if (!skcSaleAttrs.length) skcSaleAttrs = saleAttrs.filter(isColorSaleAttr);
  if (!skcSaleAttrs.length && saleAttrs.length) skcSaleAttrs = [saleAttrs[0]];

  const skcIds = new Set(skcSaleAttrs.map((a) => a.attributeId));
  let skuSaleAttrs = saleAttrs.filter((a) => !skcIds.has(a.attributeId));
  const sizeNamed = skuSaleAttrs.filter(isSizeSaleAttr);
  if (sizeNamed.length) skuSaleAttrs = [...sizeNamed, ...skuSaleAttrs.filter((a) => !sizeNamed.includes(a))];

  const requiredProduct = productAttrs.filter((a) => a.required);
  const optionalProduct = productAttrs.filter((a) => !a.required);
  // 组内保持平台 attribute_infos 顺序；名称启发式仅作兼容分组，展示以 partitionProductAttrsForDisplay 为准
  const mainHints = /季节|领型|袖|合身|面料|弹性|长度|材质|适用|场景|厚度|洗涤|门襟|下摆|腰带|里料|克重/i;
  const mainAttrs = optionalProduct.filter((a) => mainHints.test(`${a.name} ${a.nameEn}`));
  const otherAttrs = optionalProduct.filter((a) => !mainHints.test(`${a.name} ${a.nameEn}`));

  // 诊断：平台 data 行数 / attribute_infos 原始条数 / 解析后条数
  let dataRowCount = 0;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    dataRowCount = asArray(raw.data).length || asArray(raw.info?.data).length;
  } else if (Array.isArray(raw)) {
    dataRowCount = raw.length;
  }

  return {
    productAttrs,
    requiredProduct,
    mainAttrs,
    otherAttrs,
    optionalProduct,
    skcSaleAttrs,
    skuSaleAttrs,
    sizeAttrs,
    meta: {
      dataRowCount,
      poolRawCount: poolRaw.length,
      poolCount: pool.length,
      productCount: productAttrs.length,
      saleCount: saleAttrs.length,
      sizeCount: sizeAttrs.length,
    },
    raw,
  };
}

function linkedRequiredIdSet(linkedAttrRules) {
  const ids = new Set();
  if (!linkedAttrRules) return ids;
  if (linkedAttrRules instanceof Map) {
    linkedAttrRules.forEach((_, key) => {
      const id = String(key || '').trim();
      if (id) ids.add(id);
    });
    return ids;
  }
  if (Array.isArray(linkedAttrRules)) {
    linkedAttrRules.forEach((key) => {
      const id = String(key || '').trim();
      if (id) ids.add(id);
    });
    return ids;
  }
  if (typeof linkedAttrRules === 'object') {
    Object.keys(linkedAttrRules).forEach((key) => {
      const id = String(key || '').trim();
      if (id) ids.add(id);
    });
  }
  return ids;
}

/**
 * 发品/模板展示顺序：必填（平台 status=3 + 关联必填）在前，选填在后。
 * 各组内部保持平台 query-attribute-template 的相对顺序。
 */
export function partitionProductAttrsForDisplay(attrTpl, linkedAttrRules) {
  const linkedIds = linkedRequiredIdSet(linkedAttrRules);
  const seen = new Set();
  const pool = [];
  const appendPool = (list) => {
    (Array.isArray(list) ? list : []).forEach((attr) => {
      const id = String(attr?.attributeId || '').trim();
      if (!id || seen.has(id)) return;
      seen.add(id);
      pool.push(attr);
    });
  };
  if (Array.isArray(attrTpl?.productAttrs) && attrTpl.productAttrs.length) {
    appendPool(attrTpl.productAttrs);
  } else {
    appendPool(attrTpl?.requiredProduct);
    appendPool(attrTpl?.mainAttrs);
    appendPool(attrTpl?.otherAttrs);
    appendPool(attrTpl?.optionalProduct);
  }

  const requiredAttrs = [];
  const optionalAttrs = [];
  pool.forEach((attr) => {
    const id = String(attr?.attributeId || '').trim();
    if (attr?.required || linkedIds.has(id)) requiredAttrs.push(attr);
    else optionalAttrs.push(attr);
  });
  return { requiredAttrs, optionalAttrs };
}

export function attributeDisplayLabel(attr) {
  if (!attr) return '';
  const cn = simplifyPlatformLabel(attr.name);
  const en = simplifyPlatformLabel(attr.nameEn);
  if (!uiIsZh()) {
    if (en && !hasCjk(en)) return en;
    if (cn && !hasCjk(cn)) return cn;
    return fixedUi(en || cn);
  }
  if (cn && hasCjk(cn) && !isMostlyLatinWithCjkNoise(cn)) return cn;
  const mapped = ATTR_LABEL_ZH[normalizeAttrKey(cn || en)]
    || ATTR_LABEL_ZH[normalizeAttrKey((cn || en).split(/[-_(]/)[0] || '')];
  if (mapped) return mapped;
  if (cn) return cn;
  return en;
}

/** 尺码/属性值展示名：去掉「(中文)」等脏标记 */
export function attributeValueDisplayLabel(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return simplifyPlatformLabel(value) || String(value);
  }
  const raw = value.nameCn || value.name || value.label || value.id || '';
  return simplifyPlatformLabel(raw) || String(raw || '').trim();
}

/** SHEIN 销售属性：0 / 空 表示未映射平台值（自定义须改走 custom_attribute_value）。 */
export function isBlankSheinAttributeValueId(id) {
  const text = String(id ?? '').trim();
  return !text || text === '0';
}

function normalizeAttrValueMatchKey(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

/**
 * 按中文/展示名在属性模板中找回平台 attribute_value_id。
 * 刊登模板常只留下颜色名，valueId 丢失或被写成 0。
 */
export function findAttributeValueByLabel(attr, label) {
  const key = normalizeAttrValueMatchKey(label);
  if (!key) return null;
  const values = Array.isArray(attr?.values) ? attr.values : [];
  return values.find((v) => {
    const names = [v?.name, v?.nameCn, v?.nameEn, v?.label]
      .map((n) => normalizeAttrValueMatchKey(n))
      .filter(Boolean);
    return names.includes(key);
  }) || null;
}

/**
 * 用当前类目主规格模板补齐 SKC 颜色 ID。
 * 「珠宝石」这类平台色若被当成自定义（value_id=0），发品会被 SHEIN 直接拒绝。
 */
export function resolveSkcColorAgainstTemplate(skc, colorAttr) {
  if (!skc || typeof skc !== 'object') return skc;
  const attributeId = String(skc.colorAttributeId || colorAttr?.attributeId || '').trim();
  const label = String(skc.colorExtraValue || skc.colorLabel || '').trim();
  const currentId = String(skc.colorValueId ?? '').trim();
  const values = Array.isArray(colorAttr?.values) ? colorAttr.values : [];
  const knownById = !isBlankSheinAttributeValueId(currentId)
    && values.some((v) => String(v?.id) === currentId);
  if (knownById) {
    const hit = values.find((v) => String(v?.id) === currentId);
    return {
      ...skc,
      colorAttributeId: attributeId || skc.colorAttributeId,
      colorValueId: currentId,
      colorLabel: hit?.name || skc.colorLabel || label,
      colorIsCustom: false,
      colorExtraValue: '',
    };
  }
  const matched = findAttributeValueByLabel(colorAttr, label);
  if (matched?.id && !isBlankSheinAttributeValueId(matched.id)) {
    return {
      ...skc,
      colorAttributeId: attributeId || skc.colorAttributeId,
      colorValueId: String(matched.id),
      colorLabel: matched.name || label,
      colorIsCustom: false,
      colorExtraValue: '',
    };
  }
  return {
    ...skc,
    colorAttributeId: attributeId || skc.colorAttributeId,
  };
}

/** 计算完善度（粗略） */
export function calcPublishCompletion(form, attrTpl) {
  let total = 6;
  let done = 0;
  if (form.categoryId) done += 1;
  if (String(form.title || '').trim().length >= 2) done += 1;
  if (String(form.supplierCode || '').trim()) done += 1;
  if (form.skcList?.[0]?.mainImage) done += 1;
  if (form.selectedSites?.length || form.spuName) done += 1;
  if (form.skcList?.some((skc) => skc.colorValueId || skc.colorExtraValue || skc.colorLabel)) done += 1;

  const required = attrTpl?.requiredProduct || [];
  required.forEach((attr) => {
    total += 1;
    const val = form.productAttributes?.[attr.attributeId];
    if (attr.isComposition || attr.mode === 4) {
      if (Array.isArray(val) && val.length > 0) done += 1;
    } else if (attr.mode === 1) {
      if (Array.isArray(val) && val.length > 0) done += 1;
    } else if (val) {
      done += 1;
    }
  });

  return Math.min(100, Math.round((done / Math.max(total, 1)) * 100));
}
